if (!customElements.get('womanella-photo-action')) {
  let cartQueue = Promise.resolve();
  customElements.define('womanella-photo-action', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const signal = this.events.signal;
      this.trigger = this.querySelector('[data-photo-trigger]');
      this.feedback = this.querySelector('[data-photo-feedback]');
      this.selection = new Map();
      this.isOpen = false;
      this.busy = false;
      this.requestVersion = 0;
      const template = this.querySelector('template[data-photo-panel]');
      if (template) {
        this.panel = template.content.firstElementChild.cloneNode(true);
        const drawerHost = this.closest('cart-drawer');
        (drawerHost && this.panel.showPopover ? this : document.body).append(this.panel);
        if (drawerHost) {
          this.drawerObserver = new MutationObserver(() => {
            if (!drawerHost.classList.contains('active')) this.close();
          });
          this.drawerObserver.observe(drawerHost, { attributes: true, attributeFilter: ['class'] });
          drawerHost.addEventListener('scroll', event => {
            if (!this.panel.contains(event.target)) this.close();
          }, { signal, capture: true, passive: true });
        }
        this.content = this.panel.querySelector('[data-photo-options]');
        this.submit = this.panel.querySelector('[data-photo-submit]');
        this.error = this.panel.querySelector('[data-photo-error]');
        this.panel.querySelector('[data-photo-close]').addEventListener('click', () => this.close(true), { signal });
        this.panel.addEventListener('toggle', event => {
          this.isOpen = event.newState === 'open';
          this.trigger.setAttribute('aria-expanded', String(this.isOpen));
          if (!this.isOpen) this.panel.hidden = true;
        }, { signal });
        this.panel.addEventListener('keydown', event => {
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.suppressEscapeUp = true; this.close(true); }
        }, { signal });
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && this.isOpen) {
            event.preventDefault(); event.stopPropagation(); this.suppressEscapeUp = true; this.close(true);
          }
        }, { signal });
        const preventDrawerEscape = event => {
          if (event.key === 'Escape' && this.suppressEscapeUp) { event.stopPropagation(); this.suppressEscapeUp = false; }
        };
        this.addEventListener('keyup', preventDrawerEscape, { signal });
        this.panel.addEventListener('keyup', preventDrawerEscape, { signal });
        this.content.addEventListener('click', event => {
          const option = event.target.closest('button[data-option-value-id]');
          if (!option || option.disabled || this.busy) return;
          this.selection.set(option.dataset.optionPosition, option.dataset.optionValueId);
          this.loadOptions();
        }, { signal });
        this.submit.addEventListener('click', () => this.add(this.variantId, this.quantity), { signal });
        document.addEventListener('pointerdown', event => {
          if (this.isOpen && !this.panel.contains(event.target) && !this.contains(event.target)) this.close();
        }, { signal });
        window.addEventListener('resize', () => this.close(), { signal, passive: true });
        window.addEventListener('scroll', () => this.close(), { signal, passive: true });
      }
      this.trigger.addEventListener('click', () => {
        if (this.busy) return;
        this.clearFeedback();
        if (this.dataset.hasOptions === 'true') this.isOpen ? this.close(true) : this.open();
        else this.add(this.dataset.variantId, Number(this.dataset.quantity) || 1);
      }, { signal });
    }
    open() {
      this.keyboardOpen = this.trigger.matches(':focus-visible');
      this.selection.clear();
      this.content.textContent = this.dataset.loadingLabel;
      this.submit.disabled = true;
      this.error.hidden = true;
      this.panel.hidden = false;
      if (this.panel.showPopover) this.panel.showPopover();
      this.isOpen = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      this.loadOptions(true);
    }
    close(restoreFocus = false) {
      if (!this.panel || !this.isOpen) return;
      if (this.panel.hidePopover && this.panel.matches(':popover-open')) this.panel.hidePopover();
      this.panel.hidden = true;
      this.isOpen = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.isConnected) this.trigger.focus({ preventScroll: true });
    }
    position() {
      if (!this.panel || !this.isOpen) return;
      const anchor = this.trigger.getBoundingClientRect();
      const rect = this.panel.getBoundingClientRect();
      const drawerBounds = this.closest('cart-drawer')?.querySelector('.drawer__inner')?.getBoundingClientRect();
      const minimumLeft = drawerBounds ? Math.max(12, drawerBounds.left + 12) : 12;
      const maximumLeft = drawerBounds ? Math.min(innerWidth - rect.width - 12, drawerBounds.right - rect.width - 12) : innerWidth - rect.width - 12;
      const left = maximumLeft >= minimumLeft ? Math.max(minimumLeft, Math.min(maximumLeft, anchor.right - rect.width)) : Math.max(12, Math.min(innerWidth - rect.width - 12, anchor.right - rect.width));
      const above = anchor.top - rect.height - 6;
      const top = above >= 12 ? above : Math.max(12, Math.min(innerHeight - rect.height - 12, anchor.bottom + 6));
      this.panel.style.left = left + 'px';
      this.panel.style.top = top + 'px';
    }
    async loadOptions(initial = false) {
      this.optionRequest?.abort();
      this.optionRequest = new AbortController();
      const version = ++this.requestVersion;
      this.submit.disabled = true;
      this.error.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.productUrl, document.baseURI);
      url.searchParams.set('section_id', 'womanella-quick-options');
      const previewTheme = new URL(document.baseURI).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      if (!initial) {
        const ids = Array.from(this.content.querySelectorAll('fieldset')).map(group => this.selection.get(group.dataset.optionPosition) || group.querySelector('[data-server-selected="true"]')?.dataset.optionValueId || group.querySelector('[data-option-value-id]')?.dataset.optionValueId);
        if (ids.every(Boolean)) url.searchParams.set('option_values', ids.join(','));
      }
      try {
        const response = await fetch(url, { signal: this.optionRequest.signal });
        if (!response.ok) throw new Error(this.dataset.errorLabel);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const options = html.querySelector('[data-quick-options]');
        if (!options || options.dataset.productId !== this.dataset.productId) throw new Error(this.dataset.errorLabel);
        if (version !== this.requestVersion || !this.isConnected) return;
        this.content.replaceChildren(options);
        let complete = true;
        this.content.querySelectorAll('fieldset').forEach(group => {
          const position = group.dataset.optionPosition;
          const values = Array.from(group.querySelectorAll('[data-option-value-id]'));
          if (group.dataset.single === 'true') this.selection.set(position, values[0]?.dataset.optionValueId);
          const selected = values.find(value => value.dataset.optionValueId === this.selection.get(position));
          if (!selected || selected.disabled) { this.selection.delete(position); complete = false; }
          values.forEach(value => value.setAttribute('aria-pressed', String(value.dataset.optionValueId === this.selection.get(position))));
        });
        this.variantId = options.dataset.variantId;
        this.quantity = Number(options.dataset.quantity) || 1;
        this.submit.disabled = !complete || !this.variantId || options.dataset.available !== 'true';
        this.position();
        if (initial && this.keyboardOpen && this.isOpen) this.content.querySelector('fieldset:not([hidden]) button:not(:disabled)')?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.showError(error.message);
      } finally {
        if (version === this.requestVersion) this.content.removeAttribute('aria-busy');
      }
    }
    async add(variantId, quantity) {
      if (this.busy || !variantId || (this.panel && this.submit.disabled)) return;
      this.busy = true;
      clearTimeout(this.successTimer);
      this.trigger.classList.remove('is-added');
      this.clearFeedback();
      this.trigger.disabled = true;
      this.trigger.setAttribute('aria-busy', 'true');
      this.trigger.classList.add('loading');
      this.trigger.querySelector('.loading__spinner')?.classList.remove('hidden');
      if (this.submit) this.submit.disabled = true;
      this.panel?.querySelectorAll('[data-option-value-id]').forEach(button => button.disabled = true);
      const operation = async () => {
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender() || [{ id: 'cart-icon-bubble' }];
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity, sections: sections.map(section => section.id), sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.dataset.errorLabel);
        try {
          const drawerWasOpen = drawer?.classList.contains('active');
          sections.forEach(section => {
            const markup = result.sections?.[section.id];
            if (!markup) return;
            const html = new DOMParser().parseFromString(markup, 'text/html');
            const source = html.querySelector(section.selector || '.shopify-section');
            const target = document.querySelector(section.selector || '#' + section.id);
            if (source && target) target.innerHTML = source.innerHTML;
          });
          if (drawer) {
            drawer.classList.remove('is-empty');
            drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
            drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'womanella-photo-add', productVariantId: variantId, cartData: result });
          if (drawerWasOpen && drawer?.classList.contains('active')) {
            const inner = drawer.querySelector('.drawer__inner');
            if (inner && typeof trapFocus === 'function') trapFocus(inner, inner);
            if (!drawer.activeElement?.isConnected) drawer.setActiveElement(document.getElementById('cart-icon-bubble'));
          }
        } catch (error) { console.warn('Cart display update failed', error); }
        return result;
      };
      try {
        const pending = cartQueue.then(operation, operation);
        cartQueue = pending.catch(() => {});
        await pending;
        if (!this.isConnected) return;
        this.close(true);
        this.feedback.textContent = this.dataset.addedLabel;
        this.trigger.classList.add('is-added');
        clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => this.trigger.classList.remove('is-added'), 1800);
      } catch (error) {
        if (this.isConnected) this.showError(error.message);
      } finally {
        this.busy = false;
        this.trigger.disabled = false;
        this.trigger.removeAttribute('aria-busy');
        this.trigger.classList.remove('loading');
        this.trigger.querySelector('.loading__spinner')?.classList.add('hidden');
        if (this.isConnected && this.panel && this.isOpen) this.loadOptions();
      }
    }
    clearFeedback() { this.feedback.textContent = ''; this.feedback.className = 'visually-hidden'; }
    showError(message) {
      if (this.panel && this.isOpen) { this.error.textContent = message; this.error.hidden = false; this.position(); }
      else { this.feedback.className = 'womanella-photo-add__error'; this.feedback.textContent = message; }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.optionRequest?.abort();
      this.drawerObserver?.disconnect();
      clearTimeout(this.successTimer);
      this.panel?.remove();
    }
  });
}
